/**
 * Signature Capture Component
 *
 * Modal for capturing signatures with multiple input methods.
 * Features:
 * - Draw signature (canvas with signature_pad)
 * - Type signature (with font selection)
 * - Upload signature image
 * - Clear and redo
 * - Touch-friendly (44px min targets)
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import SignaturePad from 'signature_pad';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  PenTool,
  Type,
  Upload,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { Button, Dialog, DialogContent, Input } from '@respectly/ui';

type SignatureType = 'draw' | 'type' | 'upload';

interface SignatureData {
  type: SignatureType;
  data: string; // Base64 image or typed text rendered as image
  fontFamily?: string;
}

interface SignatureCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (signatureData: SignatureData) => void;
  fieldLabel?: string;
  signerName?: string;
  termsUrl?: string;
}

interface FontOption {
  name: string;
  family: string;
}

const DEFAULT_FONT: FontOption = { name: 'Script', family: "'Dancing Script', cursive" };

const SIGNATURE_FONTS: FontOption[] = [
  DEFAULT_FONT,
  { name: 'Elegant', family: "'Great Vibes', cursive" },
  { name: 'Casual', family: "'Caveat', cursive" },
  { name: 'Classic', family: "'Libre Baskerville', serif" },
];

export function SignatureCapture({
  isOpen,
  onClose,
  onSave,
  fieldLabel,
  signerName,
  termsUrl = 'https://www.respectly.ai/en/terms/',
}: SignatureCaptureProps) {
  const [activeTab, setActiveTab] = useState<SignatureType>('draw');
  const [typedName, setTypedName] = useState('');
  const [selectedFont, setSelectedFont] = useState<FontOption>(DEFAULT_FONT);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize typed name with signer name when modal opens
  useEffect(() => {
    if (isOpen && signerName && !typedName) {
      setTypedName(signerName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, signerName]);

  // Initialize signature pad
  useEffect(() => {
    if (!isOpen || activeTab !== 'draw') return;

    // Wait for dialog animation to complete and canvas to be properly rendered
    const initTimeout = setTimeout(() => {
      if (!canvasRef.current) return;

      const canvas = canvasRef.current;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);

      // Set canvas dimensions
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(ratio, ratio);
        // Fill with white background
        ctx.fillStyle = 'rgb(255, 255, 255)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Clean up previous instance
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
      }

      signaturePadRef.current = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 1,
        maxWidth: 3,
      });

      signaturePadRef.current.addEventListener('endStroke', () => {
        setHasDrawnSignature(true);
      });
    }, 350); // Wait for dialog animation

    return () => {
      clearTimeout(initTimeout);
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
    };
  }, [isOpen, activeTab]);

  // Prevent page scroll while drawing on mobile (non-passive listeners required for preventDefault)
  useEffect(() => {
    if (!isOpen || activeTab !== 'draw') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const preventScroll = (e: TouchEvent) => e.preventDefault();

    canvas.addEventListener('touchstart', preventScroll, { passive: false });
    canvas.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', preventScroll);
      canvas.removeEventListener('touchmove', preventScroll);
    };
  }, [isOpen, activeTab]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (!canvasRef.current || !signaturePadRef.current) return;

      const canvas = canvasRef.current;
      const data = signaturePadRef.current.toData();

      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(ratio, ratio);
      }

      signaturePadRef.current.clear();
      signaturePadRef.current.fromData(data);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const clearDrawing = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setHasDrawnSignature(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const renderTypedSignature = useCallback((): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 150;

    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.font = `48px ${selectedFont.family}`;
    ctx.fillStyle = 'black';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);

    return canvas.toDataURL('image/png');
  }, [typedName, selectedFont]);

  const handleSave = () => {
    let signatureData: SignatureData;

    switch (activeTab) {
      case 'draw':
        if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
          alert('Please draw your signature');
          return;
        }
        signatureData = {
          type: 'draw',
          data: signaturePadRef.current.toDataURL('image/png'),
        };
        break;

      case 'type':
        if (!typedName.trim()) {
          alert('Please type your name');
          return;
        }
        signatureData = {
          type: 'type',
          data: renderTypedSignature(),
          fontFamily: selectedFont.family,
        };
        break;

      case 'upload':
        if (!uploadedImage) {
          alert('Please upload a signature image');
          return;
        }
        signatureData = {
          type: 'upload',
          data: uploadedImage,
        };
        break;

      default:
        return;
    }

    onSave(signatureData);
  };

  const handleClose = () => {
    clearDrawing();
    setTypedName('');
    setUploadedImage(null);
    onClose();
  };

  const isValid = () => {
    switch (activeTab) {
      case 'draw':
        return hasDrawnSignature;
      case 'type':
        return typedName.trim().length > 0;
      case 'upload':
        return !!uploadedImage;
      default:
        return false;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg p-0" showCloseButton={false}>
        <div className="flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h2 className="text-lg font-semibold">
              {fieldLabel ? `Sign: ${fieldLabel}` : 'Add your signature'}
            </h2>
            <Button variant="ghost" size="sm" onClick={handleClose} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('draw')}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'draw'
                  ? 'border-b-2 border-primary-500 text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <PenTool className="h-4 w-4" />
              <span>Draw</span>
            </button>
            <button
              onClick={() => setActiveTab('type')}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'type'
                  ? 'border-b-2 border-primary-500 text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Type className="h-4 w-4" />
              <span>Type</span>
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === 'upload'
                  ? 'border-b-2 border-primary-500 text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Upload className="h-4 w-4" />
              <span>Upload</span>
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            <AnimatePresence mode="wait">
              {/* Draw Tab */}
              {activeTab === 'draw' && (
                <motion.div
                  key="draw"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="relative">
                    <canvas
                      ref={canvasRef}
                      className="h-40 w-full cursor-crosshair rounded-lg border-2 border-dashed border-gray-300 bg-white touch-none"
                      style={{ touchAction: 'none' }}
                    />
                    <p className="absolute bottom-2 left-2 text-xs text-gray-400">
                      Sign above
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearDrawing}
                      className="absolute right-2 top-2 h-8 w-8 p-0"
                      disabled={!hasDrawnSignature}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="mt-2 text-center text-sm text-gray-500">
                    Use your finger or stylus to draw your signature
                  </p>
                </motion.div>
              )}

              {/* Type Tab */}
              {activeTab === 'type' && (
                <motion.div
                  key="type"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  {/* Name Input */}
                  <div>
                    <label className="mb-1.5 block text-sm text-gray-600">
                      Signature:
                    </label>
                    <Input
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      placeholder="Type your name"
                      className="text-base"
                    />
                  </div>

                  {/* Font Selection - Vertical List */}
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {SIGNATURE_FONTS.map((font) => (
                      <button
                        key={font.name}
                        onClick={() => setSelectedFont(font)}
                        className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                          selectedFont.name === font.name
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span
                          className="text-2xl block truncate"
                          style={{ fontFamily: font.family }}
                        >
                          {typedName || signerName || 'Your Name'}
                        </span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Upload Tab */}
              {activeTab === 'upload' && (
                <motion.div
                  key="upload"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />

                  {uploadedImage ? (
                    <div className="relative">
                      <div className="rounded-lg border bg-white p-4">
                        <img
                          src={uploadedImage}
                          alt="Uploaded signature"
                          className="mx-auto max-h-40 object-contain"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUploadedImage(null)}
                        className="absolute right-2 top-2 h-8 w-8 bg-white p-0 shadow"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex h-40 w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-gray-400 hover:bg-gray-100"
                    >
                      <Upload className="h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-sm font-medium text-gray-600">
                        Click to upload signature
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        PNG, JPG up to 5MB
                      </p>
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Legal Agreement */}
          <div className="border-t px-4 py-3">
            <p className="text-xs text-gray-500 leading-relaxed">
              By electronically signing this document, I agree that my signature
              and initials are the equivalent of my handwritten signature and are
              considered originals on all documents, including legally binding
              contracts. I also agree to the{' '}
              <a
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                Terms and Conditions
              </a>
              .
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t px-4 py-3">
            <Button variant="outline" onClick={handleClose}>
              Clear
            </Button>
            <Button onClick={handleSave} disabled={!isValid()}>
              Accept and Sign
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
